"use client";

import React from "react";
import IntroHeader from "@/app/intro/components/IntroHeader";
import IntroLogos from "@/app/intro/components/IntroLogos";
import PlanDivider from "@/app/plans/components/PlanDivider";
import PlanHero from "@/app/plans/components/PlanHero";
import PlanWaitlistForm from "@/app/plans/components/PlanWaitlistForm";

type GsapCore = typeof import("gsap").gsap;

export default function SprintClient() {
	const headerRef = React.useRef<HTMLDivElement>(null);
	const heroRef = React.useRef<HTMLDivElement>(null);
	const bodyRef = React.useRef<HTMLDivElement>(null);
	const formRef = React.useRef<HTMLDivElement>(null);
	const footerRef = React.useRef<HTMLDivElement>(null);
	const gsapModuleRef = React.useRef<GsapCore | null>(null);
	const timelineRef = React.useRef<ReturnType<GsapCore["timeline"]> | null>(
		null,
	);

	React.useEffect(() => {
		const root = document.documentElement;
		root.dataset.lenisDisabled = "true";
		return () => {
			delete root.dataset.lenisDisabled;
		};
	}, []);

	React.useEffect(() => {
		if (typeof window === "undefined") return;

		let cancelled = false;

		const animate = async () => {
			if (!gsapModuleRef.current) {
				const mod = await import("gsap");
				const instance =
					(mod as unknown as { gsap?: GsapCore }).gsap ??
					(mod as unknown as { default?: GsapCore }).default ??
					(mod as unknown as GsapCore);
				gsapModuleRef.current = instance;
			}

			if (cancelled) return;

			const gsap = gsapModuleRef.current as GsapCore;
			const prefersReducedMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;

			if (prefersReducedMotion) return;

			const sections = [
				headerRef.current,
				heroRef.current,
				bodyRef.current,
				formRef.current,
				footerRef.current,
			].filter((node): node is HTMLDivElement => Boolean(node));

			if (!sections.length) return;

			timelineRef.current?.kill();

			gsap.set(sections, {
				opacity: 0,
				y: 24,
				filter: "blur(24px)",
			});

			timelineRef.current = gsap.timeline({
				defaults: { duration: 0.6, ease: "power2.out" },
			});

			timelineRef.current.to(sections, {
				opacity: 1,
				y: 0,
				filter: "blur(0px)",
				stagger: 0.12,
			});
			timelineRef.current.eventCallback("onComplete", () => {
				gsap.set(sections, { clearProps: "transform,filter" });
			});
		};

		void animate();

		return () => {
			cancelled = true;
			timelineRef.current?.kill();
		};
	}, []);

	return (
		<div className="relative flex min-h-screen flex-col overflow-x-clip bg-white">
			{/* Header */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-40 p-4">
				<div ref={headerRef}>
					<IntroHeader />
				</div>
			</div>

			{/* Top gradient (below nav) */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-30 h-20 bg-gradient-to-b from-white to-transparent" />
			{/* Bottom gradient */}
			<div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 h-20 bg-gradient-to-t from-white to-transparent" />

			{/* Page Content */}
			<div className="relative z-10 mx-auto flex w-full max-w-[588px] flex-1 flex-col items-center gap-[80px] px-6 pb-[80px] pt-[144px]">
				{/* Hero */}
				<div ref={heroRef} className="flex w-full flex-col items-center gap-10">
					<PlanHero
						title="The Sprint Plan"
						subtitle="One project, start to finish. Fixed scope, fixed price, fixed timeline."
						availabilityText="Accepting new projects"
					/>

					<PlanDivider />

					{/* Intro text */}
					<div className="flex w-full flex-col">
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								You need a launch site, a pitch deck, an MVP, a rebrand — and
								you needed it last week. The Sprint is built for exactly that:
								one project, start to finish. Fixed scope, fixed price, fixed
								timeline.
							</p>
							<p className="mt-6">
								You brief us, we deliver, we&apos;re done. No open-ended
								retainers. No scope creep. Speed and certainty. that&apos;s the
								deal.
							</p>
						</div>
					</div>
				</div>

				{/* Body */}
				<div
					ref={bodyRef}
					className="flex w-full flex-col items-center gap-[80px]"
				>
					<PlanDivider />

					{/* Trusted by logos */}
					<IntroLogos title="Trusted by industry-leaders" gap="lg" />

					<PlanDivider />

					{/* What you get */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							What you get
						</h2>
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								A dedicated designer and project manager, fully focused on your
								project for 2-4 weeks. Development included. custom design and
								custom development, everything handed off clean.
							</p>
							<p className="mt-6">
								You lead design direction. We send async updates and deliver on
								time. Same senior talent as our retainer clients. just
								concentrated into a tight window with a clear finish line.
							</p>
						</div>
					</section>

					<PlanDivider />

					{/* How it works */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							How it works
						</h2>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									1. Scope call
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									15 minutes. We nail down exactly what you need and when you
									need it. No fluff.
								</p>
							</div>
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									2. Proposal in 24 hours
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									Fixed price, fixed timeline, clear deliverables. You know
									exactly what you&apos;re getting.
								</p>
							</div>
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									3. We ship
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									Senior team heads down for 2-4 weeks. Async updates, fast
									iterations, delivered on time.
								</p>
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* Best for */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							Best for
						</h2>
						<div className="flex flex-col gap-3">
							{[
								"Product launches and landing pages",
								"Pitch decks for fundraising",
								"Brand identities and redesigns",
								"MVPs and prototypes",
								"Marketing sites that need to convert",
							].map((text) => (
								<div
									key={text}
									className="flex items-center gap-2 font-display text-base leading-6 tracking-[0.32px]"
								>
									<span className="text-[rgba(9,10,12,0.32)]">→</span>
									<span className="text-[rgba(9,10,12,0.64)]">{text}</span>
								</div>
							))}
						</div>
					</section>

					<PlanDivider />

					{/* Get started */}
					<section ref={formRef} className="flex w-full flex-col gap-10">
						<div className="flex flex-col gap-6">
							<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
								Get started
							</h2>
							<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
								Tell us what you&apos;re building. We&apos;ll tell you if a
								Sprint is the right fit and what it&apos;ll cost.
							</p>
						</div>
						<div className="border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
							<div className="flex flex-col gap-6">
								<h3 className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
									Enter waitlist
								</h3>
								<PlanWaitlistForm plan="sprint" />
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* Footer */}
					<div
						ref={footerRef}
						className="flex flex-col items-center gap-0.5 text-center"
					>
						<p className="font-display text-base font-medium leading-6 text-[rgba(9,10,12,0.56)]">
							With love,
						</p>
						<p className="font-display text-base font-semibold leading-6 tracking-[-0.16px] text-[#ff4f1a]">
							Outpace Studios™
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
